import React from 'react'
import { observer } from 'mobx-react-lite'
import { Instance } from 'mobx-state-tree'

import { Text } from '@trivie/ui-web/components/text'
import { TextField } from '@trivie/ui-web/components/text-field'
import { SubscriptionProduct } from '@trivie/core/src/models/subscriptions/SubscriptionProduct'
import { color } from '@trivie/ui-web/theme'
import { Switch } from '@mui/material'
import { CompactColorPicker } from '@trivie/ui-web/components/compact-color-picker/compact-color-picker'

interface SubscriptionDetailsProps {
  form: Instance<typeof SubscriptionProduct>
  isEditMode?: boolean
}

const placeholderLabelSx = {
  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': { color: 'rgba(0,0,0,0.4)' },
}

export const SubscriptionDetails: React.FC<SubscriptionDetailsProps> = observer(({ form }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, overflowY: 'auto', flex: 1 }}>
      <div style={{ paddingTop: 12 }}>
        <Text variant="body2" text="Title*" style={{ marginBottom: 7, color: color.shade70 }} />
        <TextField
          fullWidth
          required
          placeholder="e.g., ACLS Certification Prep"
          sx={{
            ...placeholderLabelSx,
            '& .MuiFormHelperText-root': { textAlign: 'end', marginRight: 0 },
          }}
          value={form.title}
          onBlur={() => form.touch('title')}
          onChange={(e) => form.set('title', e.target.value)}
          inputProps={{ maxLength: 64 }}
          helperText={`${form.title.length}/64`}
          error={Boolean(form.touched.get('title') && form.errors.title)}
        />
      </div>
      <div style={{ flex: 1 }}>
        <Text
          variant="body2"
          text="SKU (this field must match exactly with the product SKU from your e-commerce platform)"
          style={{ marginBottom: 7, color: color.shade70 }}
        />
        <TextField
          fullWidth
          required={form.is_active}
          placeholder="e.g., com.trivie.acls"
          sx={placeholderLabelSx}
          value={form.sku}
          onBlur={() => form.touch('sku')}
          onChange={(e) => form.set('sku', e.target.value)}
          error={Boolean(
            (form.touched.get('sku') && form.errors.sku) ||
              form.errors.sku === 'This SKU is already in use',
          )}
          helperText={
            (form.touched.get('sku') && form.errors.sku) ||
            form.errors.sku === 'This SKU is already in use'
              ? form.errors.sku
              : ''
          }
        />
      </div>
      <div>
        <Text
          variant="body2"
          text="Description (Visible to Learners)"
          style={{ marginBottom: 7, color: color.shade70 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={3}
          placeholder="Describe this subscription..."
          value={form.description}
          onChange={(e) => form.set('description', e.target.value)}
          sx={{
            ...placeholderLabelSx,
            '& .MuiInputBase-root': { padding: 0 },
            '& textarea': { resize: 'vertical' },
          }}
        />
      </div>

      <div>
        <div style={{ flexDirection: 'row', gap: 20, display: 'flex' }}>
          <div>
            <Text
              variant="body2"
              text="Duration (Days)*"
              style={{ marginBottom: 7, color: color.shade70 }}
            />
            <TextField
              fullWidth
              required
              type="number"
              placeholder="365"
              sx={placeholderLabelSx}
              value={form.duration_days}
              onBlur={() => form.touch('duration_days')}
              onChange={(e) => {
                const val = e.target.value ? parseInt(e.target.value, 10) : 0
                form.set('duration_days', val)
              }}
              inputProps={{ min: 1, max: 365 }}
              helperText={
                Boolean(form.touched.get('duration_days')) && form.errors.duration_days
                  ? form.errors.duration_days
                  : ''
              }
              error={Boolean(form.touched.get('duration_days') && form.errors.duration_days)}
            />
          </div>
          <div style={{ flex: 0.5 }}>
            <Text
              text="Color (optional)"
              variant="body2"
              style={{ marginBottom: 16, color: color.shade60 }}
              component="div"
            />
            <div style={{ flexDirection: 'row', display: 'flex', justifyContent: 'space-between' }}>
              <CompactColorPicker
                required
                helperText={
                  Boolean(form.touched.get('color') && form.errors.color) ? form.errors.color : ''
                }
                value={form.color}
                onChange={(hex) => form.set('color', hex)}
                onBlur={() => form.touch('color')}
                error={Boolean(form.touched.get('color') && form.errors.color)}
              />
            </div>
          </div>
        </div>
      </div>

      <div style={SWITCH_ROW}>
        <div style={SWITCH_TEXT}>
          <Text style={{ marginBottom: 8 }} variant="body1Medium" text="Active" />
          <Text
            variant="body2Medium"
            text={'Should this subscription be marked as active after creation?'}
            style={{ color: color.shade50, marginRight: 60 }}
          />
        </div>
        <Switch
          color="success"
          checked={form.is_active}
          onChange={(e) => form.set('is_active', e.target.checked)}
        />
      </div>
    </div>
  )
})

const SWITCH_ROW: React.CSSProperties = {
  display: 'flex',
  marginTop: 0,
  alignItems: 'center',
  justifyContent: 'space-between',
}

const SWITCH_TEXT: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
}
